import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flame, Snowflake, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { summarizeStreak, WeekStatus, FREEZE_RECHARGE_WEEKS } from '../../utils/streak';

const PANEL_WIDTH = 320;
const EDGE_GAP = 12;
// Below Tailwind's lg breakpoint (the compact header), center the panel instead of anchoring it to the badge.
const CENTER_BELOW_WIDTH = 1024;

const weeks = (n: number) => `${n} ${n === 1 ? 'week' : 'weeks'}`;

const dotClass = (status: WeekStatus, darkMode: boolean): string => {
  switch (status) {
    case 'checked':
      return 'bg-gradient-to-br from-amber-400 to-orange-500';
    case 'frozen':
      return 'bg-sky-400/80';
    case 'missed':
      return darkMode ? 'bg-slate-700' : 'bg-slate-300';
    case 'pending':
      return `border-2 border-dashed ${darkMode ? 'border-slate-600' : 'border-slate-300'}`;
    default:
      return darkMode ? 'bg-slate-800' : 'bg-slate-100';
  }
};

const dotLabel: Record<WeekStatus, string> = {
  checked: 'Checked in',
  frozen: 'Covered by streak freeze',
  missed: 'Missed',
  pending: 'This week',
  none: 'Before your streak',
};

interface StreakBadgeProps {
  compact?: boolean;
}

export const StreakBadge: React.FC<StreakBadgeProps> = ({ compact = false }) => {
  const { darkMode } = useTheme();
  const { checkIns, confirmUpToDate } = useFinancialData();
  const summary = useMemo(() => summarizeStreak(checkIns), [checkIns]);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: PANEL_WIDTH });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const lit = summary.checkedInThisWeek;

  const placePanel = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - EDGE_GAP * 2);
    const left = window.innerWidth < CENTER_BELOW_WIDTH
      ? (window.innerWidth - width) / 2
      : Math.min(Math.max(rect.right - width, EDGE_GAP), window.innerWidth - width - EDGE_GAP);
    setPosition({ top: rect.bottom + 8, left, width });
  }, []);

  useLayoutEffect(() => {
    if (open) placePanel();
  }, [open, placePanel]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [open, placePanel]);

  const badgeLabel = summary.current > 0
    ? `${summary.current}-week check-in streak`
    : 'Start your weekly check-in streak';

  const subtitle = lit
    ? "You're checked in for this week."
    : summary.current > 0
      ? 'Edit anything by Sunday to keep it going. Edits count automatically.'
      : 'Any edit you make counts automatically. Nothing changed this week? Tap below.';

  const weeksToMilestone = summary.nextMilestone ? summary.nextMilestone - summary.current : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={badgeLabel}
        aria-expanded={open}
        title={badgeLabel}
        className={`flex items-center gap-1.5 rounded-xl backdrop-blur-md transition-all whitespace-nowrap font-bold leading-none ${
          compact ? 'px-2 py-2 text-sm' : 'px-3 py-2 text-sm'
        } ${
          lit
            ? darkMode
              ? 'bg-gradient-to-br from-amber-500/25 to-orange-500/15 border border-amber-500/40 text-amber-200'
              : 'bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-300 text-amber-800'
            : darkMode
              ? 'bg-white/10 hover:bg-white/20 border border-transparent text-slate-300'
              : 'bg-slate-100 hover:bg-slate-200 border border-transparent text-slate-600'
        }`}
      >
        <Flame
          size={compact ? 18 : 16}
          className={lit ? 'text-orange-500 fill-orange-400/70' : darkMode ? 'text-slate-400' : 'text-slate-400'}
        />
        <span>{summary.current}</span>
        {!compact && <span className="hidden xl:inline font-semibold">wk</span>}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Weekly check-in streak"
          style={{ position: 'fixed', top: position.top, left: position.left, width: position.width }}
          className={`z-50 rounded-2xl p-5 shadow-2xl border ${
            darkMode ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
              lit ? 'bg-gradient-to-br from-amber-400 to-orange-500' : darkMode ? 'bg-slate-800' : 'bg-slate-100'
            }`}>
              <Flame size={26} className={lit ? 'text-white fill-white/40' : 'text-slate-400'} />
            </div>
            <div>
              <p className="text-lg font-extrabold leading-tight">
                {summary.current > 0 ? `${weeks(summary.current)} in a row` : 'Start your streak'}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subtitle}</p>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between gap-1">
              {summary.grid.map((status, i) => (
                <span
                  key={i}
                  title={dotLabel[status]}
                  className={`h-5 flex-1 rounded-md ${dotClass(status, darkMode)}`}
                />
              ))}
            </div>
            <div className={`mt-1.5 flex justify-between text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              <span>12 weeks ago</span>
              <span>This week</span>
            </div>
          </div>

          <div className={`mt-4 grid grid-cols-2 gap-2 text-sm`}>
            <div className={`rounded-xl px-3 py-2 ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
              <p className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Longest streak</p>
              <p className="font-bold">{weeks(summary.longest)}</p>
            </div>
            <div className={`rounded-xl px-3 py-2 ${darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
              <p className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Streak freeze</p>
              <p className="font-bold flex items-center gap-1">
                <Snowflake size={14} className={summary.freezeAvailable ? 'text-sky-400' : 'text-slate-400'} />
                {summary.freezeAvailable ? 'Ready' : 'Recharging'}
              </p>
            </div>
          </div>
          <p className={`mt-2 text-xs leading-relaxed ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {summary.freezeAvailable
              ? 'A freeze covers one missed week automatically.'
              : `Your freeze comes back after ${FREEZE_RECHARGE_WEEKS} weeks of checking in.`}
            {weeksToMilestone !== null && summary.current > 0 &&
              ` ${weeks(weeksToMilestone)} to your ${summary.nextMilestone}-week milestone.`}
          </p>

          {lit ? (
            <div className={`mt-4 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold ${
              darkMode ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
            }`}>
              <Check size={16} /> Checked in this week
            </div>
          ) : (
            <button
              type="button"
              onClick={confirmUpToDate}
              className="mt-4 w-full rounded-xl py-2.5 text-sm font-bold text-white bg-gradient-to-br from-[#6b38d4] to-[#8455ef] shadow-lg shadow-violet-500/20 hover:brightness-110 transition"
            >
              Everything's up to date
            </button>
          )}
          {!lit && (
            <p className={`mt-2 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Only needed when you have nothing to update.
            </p>
          )}
        </div>,
        document.body
      )}
    </>
  );
};
